import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import Button from 'components/base/Button';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import { Alert, Col, Dropdown, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { CoachProfileData } from './components/DataDefinitions';
import SearchCoachSidebar from './components/SearchCoachSidebar';
import FeatherIcon from 'feather-icons-react';
import RevealDropdown, { RevealDropdownTrigger } from 'components/base/RevealDropdown';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import useAdvanceTable from 'hooks/useAdvanceTable';
import { useState } from 'react';



const ActionDropdownItems = ({ user_id, email, onSendMessage }: {
  user_id: string | number,
  email: string,
  onSendMessage?: (user_id: string | number, message: string) => void;
}) => {

  const handleClick = () => {
    const subject = 'THEach AI: general inquiry';
    const body = 'Body text';
    window.location.href = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };


  return (
    <>
      <Dropdown.Item eventKey="1" className="text-danger">

        <p onClick={handleClick} style={{ cursor: 'pointer', color: 'blue', textDecoration: 'underline' }}>
          Send a message
        </p>
      </Dropdown.Item>

    </>
  );
};

const CoachesTable = () => {
  return (
    <div className="border-top">
      <AdvanceTable
        tableProps={{ className: 'phoenix-table fs-9' }}
        rowClassName="hover-actions-trigger btn-reveal-trigger"
      />
      <AdvanceTableFooter pagination className="py-4" />
    </div>
  );
};

const FindACoach = () => {

  const { t } = useTranslation();
  const navigate = useNavigate();
  const [users, setUsers] = useState<CoachProfileData[]>([]);

  const breadcrumbItems: PageBreadcrumbItem[] = [
    {
      label: t('Dashboard'),
      url: '/dashboard/home'
    },
    {
      label: t('Learning'),
      url: '/learning'
    },
    {
      label: t('FindACoach.page_title'),
      active: true
    }
  ];

  const coachesTableColumns: ColumnDef<CoachProfileData>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row: { original } }) => {
        const {
          first_name, last_name, is_coach, is_mentor
        } = original;
        return (

          <div className="d-flex align-items-center">

            <div>
              <Link to={`/admin/users/update/${original.user_id}`} className="fs-8 fw-bold">
                {first_name + " " + last_name}
              </Link>
              <div className="d-flex align-items-center">
                <p className="mb-0 text-1000 fw-semi-bold fs-9 me-2">
                  {is_coach && !is_mentor ? "Coach" : ""}
                  {is_mentor && !is_coach ? "Mentor" : ""}
                  {is_coach && is_mentor ? "Coach & Mentor" : ""}
                </p>
              </div>
            </div>
          </div>
        );
      },
      meta: {
        headerProps: { style: { width: '25%' }, className: 'ps-0' },
        cellProps: { className: 'white-space-nowrap ps-0' }
      }
    },
    {
      accessorKey: 'email',
      header: () => {
        return (
          <div className="d-inline-flex flex-center">
            <div className="px-1 py-1 bg-success-100 rounded me-2">
              <FeatherIcon
                icon="mail"
                size={16}
                className="text-success-600 dark__text-success-300"
              />
            </div>
            <span>Email</span>
          </div>
        );
      },
      cell: ({ row: { original } }) => {
        const { email } = original;
        return (
          <Link className="text-1000" to={`mailto:${email}`}>
            {email}
          </Link>
        );
      },
      meta: {
        headerProps: {
          style: { width: '25%' },
          className: 'ps-4 pe-5 border-end'
        },
        cellProps: {
          className: 'white-space-nowrap fw-semi-bold ps-4 border-end'
        }
      }
    },
    {
      accessorKey: 'location',
      header: () => {
        return (
          <div className="d-inline-flex flex-center">
            <span>Location</span>
          </div>
        );
      },
      cell: ({ row: { original } }) => {
        const { location } = original;
        return (
          <p className="mb-0 text-1000 fw-semi-bold fs-9 me-2">
            {location}</p>
        );
      },
      meta: {
        headerProps: {
          style: { width: '25%' },
          className: 'ps-4 pe-5 border-end'
        },
        cellProps: {
          className: 'white-space-nowrap fw-semi-bold ps-4 border-end'
        }
      }
    },
    {
      accessorKey: 'rank',
      header: () => {
        return (
          <div className="d-inline-flex flex-center">
            <span>Rank</span>
          </div>
        );
      },
      cell: ({ row: { original } }) => {
        const { rank } = original;
        return (
          <p className="mb-0 text-1000 fw-semi-bold fs-9 me-2">
            {rank}</p>
        );
      },
      meta: {
        headerProps: {
          style: { width: '5%' },
          className: 'ps-4 pe-5 border-end'
        },
        cellProps: {
          className: 'white-space-nowrap fw-semi-bold ps-4 border-end'
        }
      }
    },

    {
      id: 'userDropdown',
      cell: ({ row: { original } }) => {
        return (
          <RevealDropdownTrigger>
            <RevealDropdown>
              <ActionDropdownItems user_id={original.user_id} email={original.email} />
            </RevealDropdown>
          </RevealDropdownTrigger>
        );
      },
      meta: {
        cellProps: {
          className: 'text-end pe-0 ps-4'
        }
      }
    }
  ];

  const table = useAdvanceTable({
    data: users,
    columns: coachesTableColumns,
    pageSize: 5,
    pagination: true,
    sortable: true,
    selection: true,
    initialState: {
      columnVisibility: {
        status: false,
        designation: false
      }
    }
  });


  // Function to handle input changes
  const handleSearchComplete = (data: any) => {
    console.log(data);
    setUsers(data);
  };

  return (
    <div>
      <PageBreadcrumb items={breadcrumbItems} />
      <form className="mb-9">
        <div className="d-flex flex-wrap gap-3 flex-between-end mb-5">
          <div>
            <h2 className="mb-2">{t("FindACoach.title")}</h2>
            <h5 className="text-700 fw-semi-bold">
              {t("FindACoach.subtitle")}
            </h5>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <Button variant="phoenix-secondary" type="button">
              Discard
            </Button>
            <Button variant="phoenix-primary" type="button">
              Save draft
            </Button>
            <Button variant="primary" type="submit">
              Publish product
            </Button>
          </div>
        </div>
        <Row className="g-5">
          <Col xs={12} xl={4}>
            <Row className="g-2">
              <Col xs={12} xl={12}>
                <SearchCoachSidebar className="mb-3" onSearchComplete={handleSearchComplete} />
              </Col>
            </Row>
          </Col>
          <Col xs={12} xl={8}>

            {users.length == 0 &&


              <Alert variant="soft-primary" className='bg-200'>
                <Alert.Heading>Use filters on the left to find coaches or mentors</Alert.Heading>
                <p className="mb-0">
                  You can then contact them directly for more information.
                </p>
              </Alert>

            }

            {users.length != 0 &&
              <AdvanceTableProvider {...table}>
                <CoachesTable />
              </AdvanceTableProvider>
            }
          </Col>

        </Row>
      </form>
    </div>
  );
};

export default FindACoach;
